import { useCallback, useEffect, useMemo, useRef } from "react";
import { formatISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { ConsolidationCanvasLayoutResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import {
    consolidationQueryKeys,
    useCanvasLayout,
    useResetCanvasLayoutMutation,
    useSaveCanvasLayoutMutation,
} from "@api/consolidation.api";
import { OwnershipFlowNode } from "./ownership.types";
import {
    nextSavedPositions,
    SavedPositions,
} from "./ownershipCanvasLayout.helpers";

const SAVE_DEBOUNCE_MS = 500;

interface CommitDragsInput {
    positionedNodes: OwnershipFlowNode[];
    renderedNodes: OwnershipFlowNode[];
    drags: ReadonlyArray<{ id: string; position: { x: number; y: number } }>;
}

interface UseCanvasNodePositionsResult {
    saved: SavedPositions | null;
    isLoadingLayout: boolean;
    isFrozen: boolean;
    commitDrags: (input: CommitDragsInput) => void;
    resetLayout: () => void;
}

/**
 * Owns the shared canvas layout: reads the persisted positions, exposes them as
 * a `saved` override for ELK, and writes drags back (debounced) to the server.
 * A `null` `updatedAt` from the API means the workspace has never frozen its
 * layout, so ELK still drives everything.
 */
export function useCanvasNodePositions(
    workspaceId: string,
): UseCanvasNodePositionsResult {
    const queryClient = useQueryClient();
    const layoutQuery = useCanvasLayout(workspaceId || null);
    const saveMutation = useSaveCanvasLayoutMutation(workspaceId);
    const resetMutation = useResetCanvasLayoutMutation(workspaceId);

    const isFrozen = layoutQuery.data?.updatedAt != null;
    const saved = useMemo(
        () => (isFrozen ? (layoutQuery.data?.positions ?? null) : null),
        [isFrozen, layoutQuery.data?.positions],
    );

    const saveLayout = saveMutation.mutate;
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Latest positions written optimistically but not yet persisted — flushed on
    // unmount so a drag followed by navigating away within the debounce window
    // still reaches the server.
    const pendingSaveRef = useRef<SavedPositions | null>(null);

    const clearPending = useCallback(() => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        pendingSaveRef.current = null;
    }, []);

    const flushPending = useCallback(() => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (pendingSaveRef.current !== null) {
            saveLayout(pendingSaveRef.current);
            pendingSaveRef.current = null;
        }
    }, [saveLayout]);

    // Flush only on unmount (ref keeps the latest flush without re-running the
    // effect — and thus without saving prematurely — on every render).
    const flushRef = useRef(flushPending);
    flushRef.current = flushPending;
    useEffect(() => () => flushRef.current(), []);

    const isLoadingLayout = !!workspaceId && layoutQuery.isLoading;

    const commitDrags = useCallback(
        ({ positionedNodes, renderedNodes, drags }: CommitDragsInput) => {
            // Never freeze from ELK positions before the real layout arrives —
            // that would overwrite another user's saved layout on a slow load.
            if (drags.length === 0 || isLoadingLayout) {
                return;
            }
            const next = nextSavedPositions({
                saved,
                positionedNodes,
                renderedNodes,
                drags,
            });
            // Reflect the drag immediately so the canvas doesn't wait on the
            // network; the mutation's response later overwrites this in place.
            queryClient.setQueryData<ConsolidationCanvasLayoutResponse>(
                consolidationQueryKeys.canvasLayout(workspaceId),
                {
                    workspaceId,
                    positions: next,
                    updatedAt: formatISO(new Date()),
                },
            );
            pendingSaveRef.current = next;
            if (debounceRef.current !== null) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = setTimeout(() => {
                debounceRef.current = null;
                const pending = pendingSaveRef.current;
                pendingSaveRef.current = null;
                if (pending !== null) {
                    saveLayout(pending);
                }
            }, SAVE_DEBOUNCE_MS);
        },
        [saved, isLoadingLayout, queryClient, workspaceId, saveLayout],
    );

    const resetLayout = useCallback(() => {
        clearPending();
        resetMutation.mutate();
    }, [clearPending, resetMutation]);

    return {
        saved,
        isLoadingLayout,
        isFrozen,
        commitDrags,
        resetLayout,
    };
}
